import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 運行ダイヤ設定削除API
 */
export const useDeleteTimetableAPI = (): {
  state: APIState;
  deleteTimetable: (
    timetableId: string,
    isSuccessNotification?: boolean,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const deleteTimetableAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      timetableId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.delete(
          `/${projectId}/environments/${environmentId}/timetables/${timetableId}`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  /**
   * @return {Boolean}
   */
  const deleteTimetable = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        timetableId: string,
        isSuccessNotification: boolean | undefined = true,
      ) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.delete_time_table', { status: 'failed' }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await deleteTimetableAPI(
          project!.id,
          environment!.environment_id,
          timetableId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.delete_time_table', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          return false;
        }
        if (isSuccessNotification) {
          notifySuccess({
            message: t('api.fms.delete_time_table', { status: 'successfully' }),
            testIdAttrs: {
              wrapperTestId: 'deleteTimetable-snackbar',
              closeButtonTestId: 'deleteTimetable-snackbar-close-btn',
            },
          });
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, deleteTimetableAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    deleteTimetable,
  };
};
